import React from 'react';
import { cn } from '@/lib/cn';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Letter or string fallback to render inside the avatar when no image is provided */
  label?: string;
  /** Optional image URL. If provided, it renders instead of the label */
  src?: string | null;
  /** Visual style according to design system */
  type?: 'User' | 'Owner';
  /** Size in pixels (defaults to 40px as per design 44px container with borders) */
  size?: number;
}

/**
 * Circular avatar used for users/owners. Supports image fallback and label.
 * Background tokens:
 * - type=Owner -> bg-active (indigo-50)
 * - type=User  -> bg-secondary (slate-50)
 * Hover state deepens the background tone for both types.
 */
export function Avatar({
  label = 'A',
  src = null,
  type = 'User',
  size = 40,
  className,
  ...props
}: AvatarProps) {
  const baseBg = type === 'Owner' ? 'bg-tertiary size-11' : 'bg-active size-16';
  const hoverBg = type === 'Owner' ? '' : 'hover:bg-secondary-hover';
  const containerClasses = cn(
    'rounded-full flex items-center justify-center text-primary',
    baseBg,
    hoverBg,
  );

  return (
    <div className={cn(containerClasses, className)} {...props}>
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt={label} className="w-full h-full rounded-full object-cover" />
      ) : (
        <span className="leading-none select-none">{label}</span>
      )}
    </div>
  );
}
